import React, { useState, useEffect } from "react";
import axios from '../utils/axiosConfig';
import { useNavigate } from "react-router-dom";
import bcrypt from "bcryptjs";
import { 
  Avatar, 
  Button, 
  CssBaseline, 
  TextField, 
  Paper, 
  Box, 
  Typography, 
  Container, 
  Divider,
  Alert,
  AlertTitle,
  Collapse,
  IconButton
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/PersonAdd";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import BusinessIcon from "@mui/icons-material/Business";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import Logo from "../components/Logo";
import Footer from "../components/Footer"; // Footer importieren

const Register = () => {
  const navigate = useNavigate();
  
  // State für Formulardaten
  const [formData, setFormData] = useState({
    clubName: "",
    street: "",
    postalCode: "",
    city: "",
    name: "",
    email: "",
    emailConfirm: "",
    password: "",
    passwordConfirm: "",
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [emailFormatError, setEmailFormatError] = useState("");
  const [passwordStrengthError, setPasswordStrengthError] = useState("");
  const [formIsValid, setFormIsValid] = useState(false);
  const [alertOpen, setAlertOpen] = useState(true);

  // E-Mail-Validierung mit einem regulären Ausdruck
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Passwort-Validierung
  const validatePassword = (password: string): boolean => {
    // Mindestens 8 Zeichen, mindestens ein Großbuchstabe, ein Kleinbuchstabe und eine Zahl
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    return passwordRegex.test(password);
  };

  // Validierung E-Mail und Passwort
  useEffect(() => {
    // Validierung der E-Mail-Adresse
    if (formData.email) {
      if (!validateEmail(formData.email)) {
        setEmailFormatError("Bitte gib eine gültige E-Mail-Adresse ein.");
      } else {
        setEmailFormatError("");
      }
    }

    // Überprüfung, ob die E-Mail-Adressen übereinstimmen
    if (formData.emailConfirm && formData.email !== formData.emailConfirm) {
      setEmailError("Die E-Mail-Adressen stimmen nicht überein.");
    } else {
      setEmailError("");
    }

    // Überprüfung der Passwortstärke
    if (formData.password) {
      if (!validatePassword(formData.password)) {
        setPasswordStrengthError(
          "Das Passwort muss mindestens 8 Zeichen lang sein und mindestens einen Großbuchstaben, einen Kleinbuchstaben und eine Zahl enthalten."
        );
      } else {
        setPasswordStrengthError("");
      }
    }

    // Überprüfung, ob die Passwörter übereinstimmen
    if (formData.passwordConfirm && formData.password !== formData.passwordConfirm) {
      setPasswordError("Die Passwörter stimmen nicht überein.");
    } else {
      setPasswordError("");
    }
  }, [formData.email, formData.emailConfirm, formData.password, formData.passwordConfirm]);

  // Überprüfung der Formularvalidität
  useEffect(() => {
    // Prüfen, ob alle erforderlichen Felder ausgefüllt sind
    const requiredFieldsFilled = 
      formData.name.trim() !== "" && 
      formData.email.trim() !== "" && 
      formData.emailConfirm.trim() !== "" && 
      formData.password.trim() !== "" && 
      formData.passwordConfirm.trim() !== "" && 
      formData.clubName.trim() !== "" && 
      formData.street.trim() !== "" && 
      formData.postalCode.trim() !== "" && 
      formData.city.trim() !== "";
    
    // Prüfen, ob keine Validierungsfehler vorliegen
    const noValidationErrors = 
      !emailError && 
      !passwordError && 
      !emailFormatError && 
      !passwordStrengthError;
    
    // Formulargültigkeit aktualisieren
    setFormIsValid(requiredFieldsFilled && noValidationErrors);
  }, [
    formData,
    emailError,
    passwordError,
    emailFormatError,
    passwordStrengthError
  ]);

  // Funktion zur Änderung der Eingabefelder
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Verhindern des Einfügens in die Bestätigungsfelder
  const preventPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
  };

  // Registrierung absenden
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validierung vor dem Absenden
    if (!validateEmail(formData.email)) {
      setError("Bitte gib eine gültige E-Mail-Adresse ein.");
      return;
    }

    if (formData.email !== formData.emailConfirm) {
      setError("Die E-Mail-Adressen stimmen nicht überein.");
      return;
    }

    if (!validatePassword(formData.password)) {
      setError("Das Passwort erfüllt nicht die Sicherheitsanforderungen.");
      return;
    }

    if (formData.password !== formData.passwordConfirm) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      // 🔹 Passwort vor dem Senden hashen
      const hashedPassword = await bcrypt.hash(formData.password, 10);

      await axios.post("/api/register", {
        name: formData.name,
        email: formData.email,
        password: hashedPassword, // 🔹 Gehashtes Passwort senden
        club: {
          name: formData.clubName,
          address: {
            street: formData.street,
            postalCode: formData.postalCode,
            city: formData.city,
          },
        },
      });
      
      setMessage("Registrierung erfolgreich! Die Freischaltung muss manuell durchgeführt werden. Du erhältst eine E-Mail, sobald dein Konto aktiviert wurde.");
      setTimeout(() => navigate("/"), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || "Fehler bei der Registrierung.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh'
    }}>
      <Box sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center',
        py: 4
      }}>
        <Container component="main" maxWidth="sm">
          <CssBaseline />
          <Paper elevation={6} sx={{ padding: 4, display: "flex", flexDirection: "column", alignItems: "center", width: "100%", maxWidth: 500, mx: "auto" }}>
            <Logo height={80} />
            <Typography variant="h4" gutterBottom textAlign="center">
              Meldeportal Trampolin
            </Typography>
            <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography variant="h5" gutterBottom textAlign="center">
              Registrieren
            </Typography>
            {error && <Typography color="error">{error}</Typography>}
            {message && <Typography color="success" sx={{ textAlign: "center", mb: 2 }}>{message}</Typography>}
            
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: "100%" }}>
              {/* Ansprechpartner Sektion */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1 }}>
                <PersonOutlineIcon color="primary" />
                <Typography variant="subtitle1" color="primary" fontWeight="medium">
                  Ansprechpartner
                </Typography>
              </Box>
              
              <TextField 
                margin="normal" 
                required 
                fullWidth 
                label="Name" 
                name="name" 
                value={formData.name} 
                onChange={handleChange} 
              />
              
              {/* Zugangsdaten Sektion */}
              <Divider sx={{ mt: 3, mb: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <VpnKeyIcon color="primary" />
                <Typography variant="subtitle1" color="primary" fontWeight="medium">
                  Zugangsdaten
                </Typography>
              </Box>
              
              <TextField 
                margin="normal" 
                required 
                fullWidth 
                label="E-Mail" 
                name="email" 
                type="email"
                value={formData.email} 
                onChange={handleChange}
                error={!!emailFormatError}
                helperText={emailFormatError}
              />
              
              <TextField 
                margin="normal" 
                required 
                fullWidth 
                label="E-Mail wiederholen" 
                name="emailConfirm" 
                type="email"
                value={formData.emailConfirm} 
                onChange={handleChange} 
                onPaste={preventPaste}
                error={!!emailError}
                helperText={emailError}
              />
              
              <TextField 
                margin="normal" 
                required 
                fullWidth 
                label="Passwort" 
                type="password" 
                name="password" 
                value={formData.password} 
                onChange={handleChange} 
                error={!!passwordStrengthError}
                helperText={passwordStrengthError}
              />
              
              <TextField 
                margin="normal" 
                required 
                fullWidth 
                label="Passwort wiederholen" 
                type="password" 
                name="passwordConfirm" 
                value={formData.passwordConfirm} 
                onChange={handleChange} 
                onPaste={preventPaste}
                error={!!passwordError}
                helperText={passwordError}
              />

              {/* Vereinsdaten Sektion */}
              <Divider sx={{ mt: 3, mb: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BusinessIcon color="primary" />
                <Typography variant="subtitle1" color="primary" fontWeight="medium">
                  Vereinsdaten
                </Typography>
              </Box>

              <TextField 
                margin="normal" 
                required 
                fullWidth 
                label="Vereinsname" 
                name="clubName" 
                value={formData.clubName} 
                onChange={handleChange} 
              />
              
              <TextField 
                margin="normal" 
                required 
                fullWidth 
                label="Straße" 
                name="street" 
                value={formData.street} 
                onChange={handleChange} 
              />
              
              <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <TextField 
                  required 
                  label="PLZ" 
                  name="postalCode" 
                  value={formData.postalCode} 
                  onChange={handleChange}
                  sx={{ width: "30%" }} 
                />
                
                <TextField 
                  required 
                  label="Ort" 
                  name="city" 
                  value={formData.city} 
                  onChange={handleChange}
                  sx={{ width: "70%" }} 
                />
              </Box>

              <Button 
                type="submit" 
                fullWidth 
                variant="contained" 
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || !formIsValid}
              >
                {loading ? "Wird verarbeitet..." : "Registrieren"}
              </Button>
                        
              <Collapse in={alertOpen}>
                <Alert 
                  severity="info"
                  icon={<InfoIcon />}
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => setAlertOpen(false)}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                  sx={{ 
                    mb: 2,
                    '& .MuiAlert-message': {
                      display: 'flex',
                      flexDirection: 'column'
                    }
                  }}
                >
                  <AlertTitle>Hinweis</AlertTitle>
                  Die Registrierung kann unter Umständen etwas länger dauern, 
                  da sich die App möglicherweise im Ruhezustand befindet und neu starten muss.
                </Alert>
              </Collapse>
            </Box>


            <Box mt={2} textAlign="center">
              <Typography variant="body2">Bereits ein Konto?</Typography>
              <Button variant="outlined" sx={{ mt: 1 }} onClick={() => navigate("/")}>
                Zum Login
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
      
      {/* Footer am Ende der Seite */}
      <Footer />
    </Box>
  );
};

export default Register;